import { defineStore } from 'pinia';
import { BUNDLE_DISSOLUTION_SCENARIO } from '@apps-once/definitions';
import type { BundleDissolutionScenario } from '@apps-once/definitions';
import { useOnceStore } from '@apps-once/store';
import mushodoPromise from '@/lib/request/webapp/mushodo-promise';

type BundleDissolutionState = {
  status: { isVisible: boolean };
  scenario: BundleDissolutionScenario | null;
  addOnId: string | null;
  // Carried through so the proceed handler can re-open the cancel modal with
  // the right trial flag — the add-on store isn't primed until after the
  // warning is confirmed.
  isTrial: boolean;
  // APM add-on cancellation outstanding balance for the callout; null until fetched (or non-APM).
  outstandingBalanceIncTax: number | null;
  isLoadingOutstandingBalance: boolean;
};

export const useBundleDissolutionStore = defineStore('bundleDissolution', {
  state: (): BundleDissolutionState => ({
    status: { isVisible: false },
    scenario: null,
    addOnId: null,
    isTrial: false,
    outstandingBalanceIncTax: null,
    isLoadingOutstandingBalance: false,
  }),
  actions: {
    open(
      scenario: BundleDissolutionScenario,
      addOnId: string | null = null,
      isTrial = false
    ) {
      this.scenario = scenario;
      this.addOnId = addOnId;
      this.isTrial = isTrial;
      this.status.isVisible = true;
      this.outstandingBalanceIncTax = null;

      if (
        scenario === BUNDLE_DISSOLUTION_SCENARIO.CANCEL_ANNUAL_PAID_MONTHLY &&
        addOnId
      ) {
        void this.fetchOutstandingBalance(addOnId);
      }
    },
    // Cancelling an APM add-on charges the remaining-term balance. Preview it so the callout shows
    // the amount; a failed preview leaves the static copy in place.
    async fetchOutstandingBalance(addOnId: string): Promise<void> {
      this.outstandingBalanceIncTax = null;
      this.isLoadingOutstandingBalance = true;

      const onceStore = useOnceStore();
      try {
        const result = await mushodoPromise('PreviewCancellation', {
          Hostname: onceStore.hostname,
          AddOnProductIdentifier: addOnId,
        });
        if (result && !result.error) {
          this.outstandingBalanceIncTax = result.outstandingBalanceIncTax;
        }
      } catch {
        this.outstandingBalanceIncTax = null;
      } finally {
        this.isLoadingOutstandingBalance = false;
      }
    },
    close() {
      this.status.isVisible = false;
      this.scenario = null;
      this.addOnId = null;
      this.isTrial = false;
      this.outstandingBalanceIncTax = null;
      this.isLoadingOutstandingBalance = false;
    },
  },
});
